'use client'

import { format, parseISO } from 'date-fns'
import { CalendarPlus, CheckCircle2 } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useEffect, useMemo, useRef } from 'react'

import { useRedeemT } from '../i18n/useRedeemT'
import type { IBookingDetail } from '../types/schema'
import { buildGoogleCalendarUrl, downloadICS } from '../utils/calendar'
import { decodeEntities } from '../utils/decodeEntities'
import { formatDuration } from '../utils/timeSlots'
import { CARD, CTA_PRIMARY, CTA_SECONDARY } from './styles'

interface Props {
  detail: IBookingDetail
  onStartOver: () => void
}

export default function ConfirmSection({ detail, onStartOver }: Props) {
  const t = useRedeemT()
  const headingRef = useRef<HTMLHeadingElement>(null)

  // No navigation announces this on a single-scroll page, so move focus here — otherwise a screen
  // reader user is left on a form that silently vanished.
  useEffect(() => {
    headingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    headingRef.current?.focus()
  }, [])

  const apptDate = useMemo(() => {
    const parsed = parseISO(detail.appt_date)
    return Number.isNaN(parsed.valueOf()) ? detail.appt_date : format(parsed, 'EEEE, d MMMM yyyy')
  }, [detail.appt_date])

  const apptTime = detail.appt_time?.slice(0, 5) ?? ''
  // Was sliced to "02:00" and shown under "Duration" with no unit at all.
  const totalHours = formatDuration(detail.total_hours ?? '')

  // `product` arrives HTML-entity-encoded from the DB.
  const product = decodeEntities(detail.gift_used?.[0]?.product ?? '')
  const giftNumber = detail.gift_used?.[0]?.gift_number ?? ''

  const calendarTitle = `Oasis Spa — ${product || 'Treatment'}`
  const googleUrl = buildGoogleCalendarUrl(detail, calendarTitle)


  const handleAddToCalendar = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isMobileDevice()) return
    if (downloadICS(detail, calendarTitle)) e.preventDefault()
  }

  return (
    <section className='container mx-auto max-w-4xl px-4 py-6'>
      <div className={`${CARD} p-6 md:p-10`}>
        <div className='mb-6 flex items-center gap-4'>
          <div className='grid h-14 w-14 shrink-0 place-items-center rounded-full bg-theme-light'>
            <CheckCircle2 className='h-7 w-7 text-theme' />
          </div>
          <div className='min-w-0'>
            <h2
              ref={headingRef}
              tabIndex={-1}
              className='text-2xl font-bold text-gray-900 outline-none'>
              {t('TITLE_CONGRATS_DATE', { date: apptDate })}
            </h2>
            <p className='text-sm text-gray-500'>
              {detail.cust_email
                ? t('DESCRIPTION_CONGRATS_EMAIL', { email: detail.cust_email })
                : t('DESCRIPTION_CONGRATS_NO_EMAIL')}
            </p>
          </div>
        </div>

        <dl className='grid grid-cols-2 gap-4 border-t border-gray-100 pt-6 md:grid-cols-4'>
          <Stat label={t('LABEL_BOOKING_NUMBER')} value={String(detail.bpds_id)} big />
          <Stat label={t('LABEL_LOCATION')} value={detail.branch_name} />
          <Stat label={t('LABEL_DATE_TIME')} value={`${apptDate} · ${apptTime}`} />
          <Stat label={t('LABEL_DURATION')} value={totalHours} />
          {giftNumber && <Stat label={t('LABEL_GIFT_NUMBER')} value={giftNumber} />}
          {detail.cust_name && <Stat label={t('LABEL_CUSTOMER_NAME')} value={detail.cust_name} />}
          {detail.cust_phone && <Stat label={t('LABEL_CUSTOMER_PHONE')} value={detail.cust_phone} />}
        </dl>

        <div className='mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700'>
          <strong className='text-gray-900'>{t('TITLE_WHAT_TO_BRING')}</strong>{' '}
          {t('DESCRIPTION_WHAT_TO_BRING')}
        </div>

        <div className='mt-6 flex flex-col gap-3 md:flex-row'>
          {googleUrl && (
            <a
              href={googleUrl}
              target='_blank'
              rel='noopener noreferrer'
              onClick={handleAddToCalendar}
              className={`${CTA_PRIMARY} flex-1`}>
              <CalendarPlus className='h-4 w-4' /> {t('BUTTON_ADD_CALENDAR')}
            </a>
          )}
          <button type='button' onClick={onStartOver} className={`${CTA_SECONDARY} flex-1`}>
            {t('BUTTON_START_OVER')}
          </button>
        </div>
      </div>
    </section>
  )
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent

  return (
    /android|iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  )
}

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }) {
  return (
    <div>
      <dt className='text-[11px] font-bold uppercase tracking-wider text-gray-500'>{label}</dt>
      <dd
        className={`mt-1 ${big ? 'text-lg font-bold text-theme-orange' : 'text-sm font-medium text-gray-800'
          }`}>
        {value}
      </dd>
    </div>
  )
}
